import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/feature/SectionHeading';
import useSeo from '@/hooks/useSeo';

const friendLinks = [
  { name: '亚洲影展', desc: '区域影视展映与交流平台' },
  { name: '影视资料库', desc: '影视条目与幕后资料汇总' },
  { name: '电影观察周刊', desc: '影评、专访与行业观察' },
  { name: '剧集前沿', desc: '电视剧集资讯与解读' },
];

const scopeItems = [
  { icon: 'ri-movie-2-line', title: '作品资料', desc: '按类型、年份与角色整理作品条目，附简介与角色说明。' },
  { icon: 'ri-user-star-line', title: '明星档案', desc: '收录基本资料、生平简介与演艺历程的时间线整理。' },
  { icon: 'ri-newspaper-line', title: '影视动态', desc: '汇总作品消息、深度解读与人物专访，持续更新。' },
  { icon: 'ri-search-eye-line', title: '站内检索', desc: '支持按作品、动态与资料分类检索，快速定位内容。' },
];

export default function About() {
  useSeo({
    title: '关于本站 - 吉泽明步影视资料站',
    description:
      '关于吉泽明步影视资料站：本站围绕影视演员吉泽明步的资料与作品展开，提供作品介绍、明星档案与影视动态，内容仅作展示用途。',
    keywords: '关于本站,吉泽明步,影视资料站,网站介绍,联系方式',
  });

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于' }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            关于本站
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            本站以「专业资料风」为定位，围绕影视演员吉泽明步的资料与作品展开整理。
          </p>
        </div>
      </div>

      <div className="container-page py-10 md:py-14">
        <section className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">网站定位</h2>
            <div className="mt-4 space-y-4 text-base leading-loose text-foreground-700">
              <p>
                吉泽明步影视资料站是一个非商业性质的资料整理站点，目标是为影迷与影视爱好者提供一个结构清晰、便于查询的信息入口。
                我们希望通过克制的视觉语言与明确的信息层级，让查阅作品与资料的过程更轻松。
              </p>
              <p>
                站内所有作品条目、资料与动态均用于展示与演示目的，不构成任何官方发布。如涉及内容纠错或补充建议，
                欢迎通过下方联系方式与我们联系。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/works"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
              >
                浏览作品展示
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to="/profile"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                查看明星资料
              </Link>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">联系方式</h3>
            <ul className="mt-4 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-foreground-500">邮箱</p>
                  <p className="font-medium text-foreground-900">contact@akiho-archive.jp</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-map-pin-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-foreground-500">所在地</p>
                  <p className="font-medium text-foreground-900">日本 · 东京</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-foreground-500">响应时间</p>
                  <p className="font-medium text-foreground-900">工作日 10:00 - 18:00</p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="mt-14 md:mt-16">
          <SectionHeading eyebrow="Content Scope" title="内容范围" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {scopeItems.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 md:mt-16">
          <SectionHeading eyebrow="Friend Links" title="友情链接" desc="与影视资讯相关的合作与推荐站点。" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {friendLinks.map((link) => (
              <div
                key={link.name}
                className="group flex items-center justify-between gap-4 rounded-lg border border-background-200/70 bg-background-50 px-5 py-4 transition-colors hover:border-primary-300"
              >
                <div className="min-w-0">
                  <h3 className="font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {link.name}
                  </h3>
                  <p className="mt-1 text-sm text-foreground-600">{link.desc}</p>
                </div>
                <i className="ri-external-link-line shrink-0 text-foreground-400 group-hover:text-primary-600 transition-colors" aria-hidden="true"></i>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-lg border border-background-200/70 bg-background-100/50 p-6 md:mt-16 md:p-8">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">版权与免责说明</h2>
          <p className="mt-3 text-sm leading-loose text-foreground-600">
            本站为前端演示站点，所有作品条目、人物资料与动态内容均为展示用途的模拟数据，不代表任何真实发布或立场。
            站内图片为示意性素材，仅用于界面展示。若你需要将本站用于正式场景，请自行替换为合规的内容与素材，并遵守当地相关法律法规。
          </p>
          <p className="mt-3 text-xs text-foreground-500">© 2023 吉泽明步影视资料站 · 保留所有权利</p>
        </section>
      </div>
    </div>
  );
}